import { Box, IconButton, Typography } from '@mui/material'
import i18n from 'i18n-js'
import { useEffect, useRef, useState } from 'react'
import { Icons } from '../../utils'
import { styles } from './styles'

const PhotoPicker = ({
  value = [],
  onChange,
  maxFiles = 10,
  maxSizeMB = 5,
  acceptTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'],
  disabled = false,
  label,
}) => {
  const inputRef = useRef(null)
  const [errors, setErrors] = useState([])
  const [previews, setPreviews] = useState([])

  useEffect(() => {
    const urls = value.map((file) => URL.createObjectURL(file))
    setPreviews(urls)

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [value])

  const handleFiles = (e) => {
    const selected = Array.from(e.target.files || [])
    const nextErrors = []
    const accepted = []

    selected.forEach((file) => {
      if (!acceptTypes.includes(file.type)) {
        nextErrors.push({
          key: 'observation.photos.invalidTypeError',
          params: { fileName: file.name },
        })
        return
      }
      if (file.size > maxSizeMB * 1024 * 1024) {
        nextErrors.push({
          key: 'observation.photos.maxSizeError',
          params: { fileName: file.name, maxSize: maxSizeMB },
        })
        return
      }
      if (value.length + accepted.length >= maxFiles) {
        nextErrors.push({
          key: 'observation.photos.maxCountError',
          params: { max: maxFiles },
        })
        return
      }
      accepted.push(file)
    })

    setErrors(nextErrors)
    if (accepted.length > 0) {
      onChange([...value, ...accepted])
    }
    if (inputRef.current) {
      inputRef.current.value = null
    }
  }

  const handleRemove = (index) => {
    setErrors([])
    onChange(value.filter((_, i) => i !== index))
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <IconButton
          sx={styles.addButton}
          disabled={disabled || value.length >= maxFiles}
          onClick={() => inputRef.current?.click()}
          aria-label={label}
        >
          {Icons('camera')}
        </IconButton>

        <Box>
          {label && (
            <Typography variant="body2" sx={styles.label}>
              {label}
            </Typography>
          )}
          <Typography variant="caption" color="textSecondary" sx={styles.hint}>
            {i18n.t('observation.photos.hint', {
              count: value.length,
              max: maxFiles,
              maxSize: maxSizeMB,
            })}
          </Typography>
        </Box>
      </Box>

      {errors.length > 0 && (
        <Box sx={styles.errors}>
          {errors.map((error, index) => (
            <Typography
              key={`${error.key}-${index}`}
              variant="caption"
              color="error"
              sx={styles.errorText}
            >
              {i18n.t(error.key, error.params)}
            </Typography>
          ))}
        </Box>
      )}

      {previews.length > 0 && (
        <Box sx={styles.grid}>
          {previews.map((url, index) => (
            <Box key={url} sx={styles.thumb}>
              <Box component="img" src={url} sx={styles.thumbImg} alt="" />
              <IconButton
                size="small"
                sx={styles.removeButton}
                onClick={() => handleRemove(index)}
                disabled={disabled}
              >
                {Icons('close')}
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        accept={acceptTypes.join(',')}
        onChange={handleFiles}
        disabled={disabled}
      />
    </Box>
  )
}

export default PhotoPicker
